import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslate, useNavigation } from "@refinedev/core";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
} from "@mui/material";
import axiosInstance from "../../api/axios-config";
import { useAuth } from "../../contexts/auth-context";
import { useIPLocation } from "../../hooks/useIPLocation";
import { useUnifiedMonitoringEvents } from "../../hooks/useMonitoringEvents";
import { ServiceWorkerStatusIndicator } from "../../components/ServiceWorkerStatusIndicator";
import { EventCardStrip } from "./components/EventCardStrip";
import { UnifiedMap } from "./components/UnifiedMap";
import { UnifiedTimeline } from "./components/UnifiedTimeline";
import type { MapLayerVisibility } from "./components/MapLayerToggles";
import { isAlertEvent, isJourneyEvent, isForbiddenZoneEvent } from "../../utils/unifiedEvents";
import type { UnifiedEvent, UnifiedAlertEvent, UnifiedJourneyEvent, UnifiedForbiddenZoneEvent } from "../../types/events";

// ── Demo mode: set to true to inject mock events for UI design preview ──
const DEMO_MODE = false;

function createDemoEvents(): { active: UnifiedEvent[]; ended: UnifiedEvent[] } {
  const now = new Date().toISOString();
  const ago = (minutes: number) => new Date(Date.now() - minutes * 60_000).toISOString();

  const demoActive: UnifiedEvent[] = [
    // Active broadcasting alert — Paris
    {
      key: "alert-demo-1",
      eventType: "alert",
      sortPriority: 0,
      timestamp: ago(3),
      isActive: true,
      userName: "Marie Dupont",
      latitude: 48.8566,
      longitude: 2.3522,
      data: {
        id: 9001,
        user: { id: 9001, fullname: "Marie Dupont", email: "marie@demo.com" },
        latitude: 48.8566,
        longitude: 2.3522,
        createdAt: ago(3),
        updatedAt: now,
        isBroadcasting: true,
        status: "NEW",
      },
    } as UnifiedAlertEvent,
    // Accepted alert — Lyon
    {
      key: "alert-demo-2",
      eventType: "alert",
      sortPriority: 0,
      timestamp: ago(12),
      isActive: true,
      userName: "Lucas Martin",
      latitude: 45.764,
      longitude: 4.8357,
      data: {
        id: 9002,
        user: { id: 9002, fullname: "Lucas Martin", email: "lucas@demo.com" },
        latitude: 45.764,
        longitude: 4.8357,
        createdAt: ago(12),
        updatedAt: now,
        isBroadcasting: false,
        status: "ACCEPTED",
      },
    } as UnifiedAlertEvent,
    // Active journey — Marseille to Nice
    {
      key: "journey-demo-1",
      eventType: "journey",
      sortPriority: 2,
      timestamp: ago(45),
      isActive: true,
      userName: "Sophie Bernard",
      latitude: 43.6047,
      longitude: 5.9876,
      data: {
        id: 9003,
        user: { id: 9003, fullname: "Sophie Bernard", email: "sophie@demo.com" },
        originLatitude: 43.2962,
        originLongitude: 5.3832,
        currentLatitude: 43.6047,
        currentLongitude: 5.9876,
        destinationLatitude: 43.7102,
        destinationLongitude: 7.262,
        createdAt: ago(45),
        updatedAt: now,
        lastHeartbeat: ago(1),
        hasEnded: false,
      },
    } as UnifiedJourneyEvent,
    // Active forbidden zone — Toulouse
    {
      key: "forbidden_zone-demo-1",
      eventType: "forbidden_zone",
      sortPriority: 1,
      timestamp: ago(8),
      isActive: true,
      userName: "Emma Petit",
      latitude: 43.6047,
      longitude: 1.4442,
      data: {
        id: 9004,
        placeId: 901,
        userId: 9004,
        latitude: 43.6047,
        longitude: 1.4442,
        enteredAt: ago(8),
        exitedAt: null,
        notifiedUserIds: [],
        place: { id: 901, name: "Zone industrielle", emoji: "🏭" },
        user: { id: 9004, fullname: "Emma Petit", email: "emma@demo.com" },
      },
    } as UnifiedForbiddenZoneEvent,
  ];

  const demoEnded: UnifiedEvent[] = [
    // Closed alert
    {
      key: "alert-demo-3",
      eventType: "alert",
      sortPriority: 0,
      timestamp: ago(60),
      isActive: false,
      userName: "Jean Moreau",
      latitude: 48.5734,
      longitude: 7.7521,
      data: {
        id: 9005,
        user: { id: 9005, fullname: "Jean Moreau", email: "jean@demo.com" },
        latitude: 48.5734,
        longitude: 7.7521,
        createdAt: ago(90),
        updatedAt: ago(60),
        isBroadcasting: false,
        status: "CLOSED",
      },
    } as UnifiedAlertEvent,
    // Called alert
    {
      key: "alert-demo-4",
      eventType: "alert",
      sortPriority: 0,
      timestamp: ago(120),
      isActive: false,
      userName: "Claire Leroy",
      latitude: 44.8378,
      longitude: -0.5792,
      data: {
        id: 9006,
        user: { id: 9006, fullname: "Claire Leroy", email: "claire@demo.com" },
        latitude: 44.8378,
        longitude: -0.5792,
        createdAt: ago(150),
        updatedAt: ago(120),
        isBroadcasting: false,
        status: "CALLED",
      },
    } as UnifiedAlertEvent,
    // Ended journey
    {
      key: "journey-demo-2",
      eventType: "journey",
      sortPriority: 2,
      timestamp: ago(180),
      isActive: false,
      userName: "Thomas Roux",
      latitude: 47.2184,
      longitude: -1.5536,
      data: {
        id: 9007,
        user: { id: 9007, fullname: "Thomas Roux", email: "thomas@demo.com" },
        originLatitude: 48.8566,
        originLongitude: 2.3522,
        currentLatitude: 47.2184,
        currentLongitude: -1.5536,
        destinationLatitude: 47.2184,
        destinationLongitude: -1.5536,
        createdAt: ago(300),
        updatedAt: ago(180),
        lastHeartbeat: ago(180),
        hasEnded: true,
      },
    } as UnifiedJourneyEvent,
    // Exited forbidden zone
    {
      key: "forbidden_zone-demo-2",
      eventType: "forbidden_zone",
      sortPriority: 1,
      timestamp: ago(200),
      isActive: false,
      userName: "Camille Faure",
      latitude: 43.6107,
      longitude: 3.8767,
      data: {
        id: 9008,
        placeId: 902,
        userId: 9008,
        latitude: 43.6107,
        longitude: 3.8767,
        enteredAt: ago(240),
        exitedAt: ago(200),
        notifiedUserIds: [],
        place: { id: 902, name: "Parc interdit", emoji: "🚫" },
        user: { id: 9008, fullname: "Camille Faure", email: "camille@demo.com" },
      },
    } as UnifiedForbiddenZoneEvent,
  ];

  return { active: demoActive, ended: demoEnded };
}

export const MonitoringList: React.FC = () => {
  const { user: currentUser } = useAuth();
  const translate = useTranslate();
  const { show } = useNavigation();
  const { location: ipLocation } = useIPLocation();

  // Unified data from all 3 sources (split polling built-in)
  const {
    activeEvents,
    endedEvents,
    isLoading,
    alertCount,
    journeyCount,
    forbiddenZoneCount,
    forbiddenZoneDefinitions,
  } = useUnifiedMonitoringEvents();

  // UI state
  const [focusedEventKey, setFocusedEventKey] = useState<string | null>(null);
  const [hoveredEventKey, setHoveredEventKey] = useState<string | null>(null);
  const [eventLocation, setEventLocation] = useState<[number, number] | null>(null);
  const [layerVisibility, setLayerVisibility] = useState<MapLayerVisibility>({
    alerts: true,
    journeys: true,
    forbiddenZones: true,
  });
  const [showZoneBoundaries, setShowZoneBoundaries] = useState(true);

  // Fetch active security events to auto-center map on user's event
  useEffect(() => {
    if (!currentUser?.id) return;

    const fetchSecurityEventLocation = async () => {
      try {
        const response = await axiosInstance.get("/safe-events", {
          params: { isActive: "true" },
        });
        const events = response.data;
        if (Array.isArray(events) && events.length > 0) {
          const userEvent = events.find(
            (event: any) =>
              event.securityContacts?.some(
                (contact: any) => contact.id === currentUser.id
              )
          );
          if (userEvent?.latitude && userEvent?.longitude) {
            setEventLocation([userEvent.latitude, userEvent.longitude]);
          }
        }
      } catch {
        // Silently fail — non-critical
      }
    };

    fetchSecurityEventLocation();
  }, [currentUser]);

  const handleFocusToggle = useCallback((key: string) => {
    setFocusedEventKey((prev) => (prev === key ? null : key));
  }, []);

  const handleHoverEvent = useCallback((key: string | null) => {
    setHoveredEventKey(key);
  }, []);

  const handleLayerChange = useCallback((layer: keyof MapLayerVisibility) => {
    setLayerVisibility((prev) => ({ ...prev, [layer]: !prev[layer] }));
  }, []);

  const handleCardNavigate = useCallback((event: UnifiedEvent) => {
    if (isAlertEvent(event)) {
      if (event.data.isBroadcasting) {
        show("monitoring", event.data.id);
      } else {
        window.open(`/history/alert/${event.data.id}`, "_blank");
      }
    } else if (isJourneyEvent(event)) {
      window.open(`/history/journey/${event.data.id}`, "_blank");
    } else if (isForbiddenZoneEvent(event)) {
      window.open(`/history/forbidden-zone/${event.data.id}`, "_blank");
    }
  }, [show]);

  // Merge demo events when DEMO_MODE is enabled
  const demoEvents = useMemo(() => DEMO_MODE ? createDemoEvents() : { active: [], ended: [] }, []);

  const mergedActiveEvents = useMemo(
    () => DEMO_MODE ? [...demoEvents.active, ...activeEvents] : activeEvents,
    [activeEvents, demoEvents.active],
  );
  const mergedEndedEvents = useMemo(
    () => DEMO_MODE ? [...demoEvents.ended, ...endedEvents] : endedEvents,
    [endedEvents, demoEvents.ended],
  );

  const mergedAlertCount = alertCount + (DEMO_MODE ? 2 : 0);
  const mergedJourneyCount = journeyCount + (DEMO_MODE ? 1 : 0);
  const mergedForbiddenZoneCount = forbiddenZoneCount + (DEMO_MODE ? 1 : 0);

  // Only active/live events on the map
  const mapEvents = mergedActiveEvents;

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: { xs: "50vh", md: "60vh" } }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { xs: "calc(100vh - 80px)", md: "calc(100vh - 115px)" },
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Title bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: { xs: 1, md: 2 },
          py: { xs: 0.5, md: 1 },
          flexShrink: 0,
        }}
      >
        <Typography
          variant="h5"
        >
          {mergedActiveEvents.length} {translate("monitoring.eventsInProgress", "active events")}
        </Typography>
        <ServiceWorkerStatusIndicator />
      </Box>

      {/* Card strip — active events */}
      <Box sx={{ flexShrink: 0 }}>
        <EventCardStrip
          events={mergedActiveEvents}
          focusedEventKey={focusedEventKey}
          onFocusToggle={handleFocusToggle}
          onNavigate={handleCardNavigate}
          onHoverEvent={handleHoverEvent}
        />
      </Box>

      {/* Bottom: Map + Timeline */}
      <Box sx={{ flex: 1, overflow: "hidden", px: { xs: 0.5, md: 1 }, pt: { xs: 1, md: 2 }, pb: { xs: 0.5, md: 1 } }}>
        <Grid
          container
          spacing={{ xs: 1, md: 2 }}
          sx={{ height: "100%", margin: 0, width: "100%" }}
        >
          {/* Map — 75% on xl, 67% on md */}
          <Grid
            item
            xs={12}
            md={8}
            lg={8}
            xl={9}
            sx={{
              height: { xs: "50%", md: "100%" },
              paddingTop: "0 !important",
              paddingLeft: "0 !important",
            }}
          >
            <UnifiedMap
              events={mapEvents}
              ipLocation={ipLocation}
              eventLocation={eventLocation}
              hoveredEventKey={hoveredEventKey}
              layerVisibility={layerVisibility}
              onLayerChange={handleLayerChange}
              showZoneBoundaries={showZoneBoundaries}
              onToggleZoneBoundaries={() => setShowZoneBoundaries((prev) => !prev)}
              alertCount={mergedAlertCount}
              journeyCount={mergedJourneyCount}
              forbiddenZoneCount={mergedForbiddenZoneCount}
              forbiddenZoneDefinitions={forbiddenZoneDefinitions}
            />
          </Grid>

          {/* Timeline — 25% on xl, 33% on md */}
          <Grid
            item
            xs={12}
            md={4}
            lg={4}
            xl={3}
            sx={{
              height: { xs: "50%", md: "100%" },
              paddingTop: { xs: "8px !important", md: "0 !important" },
              paddingLeft: "0 !important",
            }}
          >
            <Box sx={{ height: "100%", minHeight: { xs: "250px", md: "400px" } }}>
              <UnifiedTimeline events={mergedEndedEvents} />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
